import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { ContentService } from 'src/app/views/content/content.service';
import { AuthService } from 'src/app/auth.service';
import { EnvService } from 'src/app/env.service';
import { ChunkService } from 'src/app/views/content/chunk/chunk.service';
import { WhiteboardService } from './whiteboard.service';
import { environment } from 'src/environments/environment';
import { v4 as uuidv4 } from 'uuid';
import { ToastService } from '../../services/toast/toast.service';
import { DrawItem, Point } from './whiteboard.models';
import { PostIt } from './postit.models';

@Component({
  selector: 'app-whiteboard-part',
  templateUrl: './whiteboard-part.component.html',
  styleUrls: ['./whiteboard-part.component.css'],
  standalone: true,
  imports: [MatIconModule, MatButtonModule, TranslateModule],
})
export class WhiteboardPartComponent implements OnInit, OnDestroy {
  @Input() color;
  @Input() backgroundColor;
  @Input() boardID;
  @Input() origin;
  @Input() chunkParams;
  @Input() postits: PostIt[] = [];
  @Input() readOnly = false;
  @Input() renderPostItsOnCanvas = false;
  private _zoomScale = 1.0;
  @Input() set zoomScale(value: number) {
    this._zoomScale = value;
    this.redrawAll();
  }
  get zoomScale() { return this._zoomScale; }

  private _panX = 0;
  @Input() set panX(value: number) {
    this._panX = value;
    this.redrawAll();
  }
  get panX() { return this._panX; }

  private _panY = 0;
  @Input() set panY(value: number) {
    this._panY = value;
    this.redrawAll();
  }
  get panY() { return this._panY; }

  @Input() size;
  @Input() tool: 'freehand' | 'eraser' = 'freehand';
  @Output() strokeAdded = new EventEmitter<DrawItem>();

  layoutConfSub: Subscription;
  canvas;
  ctx: CanvasRenderingContext2D;
  drawing = false;
  mouseDown = false;
  canvasPosition;
  boardEvent: Subscription;
  private _drawHistory: DrawItem[] = [];
  @Input() set drawHistory(value: DrawItem[]) {
    this._drawHistory = value || [];
    this.redrawAll();
  }
  get drawHistory(): DrawItem[] {
    return this._drawHistory;
  }
  undoCount = 0;
  currentStroke: DrawItem | null = null;
  @ViewChild('board', { static: true }) board: ElementRef<HTMLCanvasElement>;
  private resizeObserver: ResizeObserver | null = null;

  constructor(
    private streamingService: WhiteboardService,
    private authService: AuthService,
    private envService: EnvService,
    private contentService: ContentService,
    private chunkService: ChunkService,
    private toast: ToastService,
    private translate: TranslateService
  ) {
    this.layoutConfSub = this.boardEvent = this.streamingService.boardEvent$.subscribe((res) => {
      // console.log(res, this.boardID)
      if (res.type == 'resize') {
        this.onResize();
      }
      if (res.boardID == this.boardID) {
        switch (res.type) {
          case 'erase':
            this.eraseBoard(true);
            break;
          case 'undo':
            this.undo();
            break;
          case 'redo':
            this.redo();
            break;
          case 'save':
            this.download();
            break;
          case 'stroke':
            if (res.stroke) {
              if (this.undoCount > 0) {
                this.drawHistory.length = this.drawHistory.length - this.undoCount;
                this.undoCount = 0;
              }
              this.drawHistory.push(res.stroke);
              this.redrawAll();
            }
            break;
          case 'sync-history':
            if (res.history) {
              this.drawHistory = res.history;
              this.undoCount = 0;
              this.redrawAll();
            }
            break;
        }
      }
    });
  }

  ngOnInit() {
    this.getContext();
    this.resizeObserver = new ResizeObserver(() => {
      this.onResize();
    });
    this.resizeObserver.observe(this.board.nativeElement);
    document.addEventListener('keydown', this.onKeyPressedBound);
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    document.removeEventListener('keydown', this.onKeyPressedBound);
    this.boardEvent.unsubscribe();
  }

  onKeyPressedBound = (e: KeyboardEvent) => this.onKeyPressed(e);

  onKeyPressed(event: KeyboardEvent) {
    if (event.ctrlKey && event.key === 'z') {
      this.undo();
    }
    if (event.ctrlKey && event.shiftKey && (event.key === 'z' || event.key === 'Z')) {
      this.redo();
    }
  }

  onResize() {
    this.getContext();
    this.redrawAll();
  }

  eraseBoard(recordEraseAction = false) {
    if (recordEraseAction) {
      this.drawHistory = [];
      this.undoCount = 0;
      this.postits = [];
    }
    this.redrawAll();
  }

  undo() {
    if (this.undoCount < this.drawHistory.length) {
      this.undoCount++;
      this.redrawAll();
    }
  }

  redo() {
    if (this.undoCount > 0) {
      this.undoCount--;
      this.redrawAll();
    }
  }

  redrawAll() {
    if (!this.ctx) return;
    const canvasEl = this.board.nativeElement;
    const ratio = window.devicePixelRatio || 1;
    
    // Reset transform to identity and clear
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);

    this.ctx.save();
    // Apply DPI scaling
    this.ctx.scale(ratio, ratio);
    // Apply pan and zoom
    this.ctx.translate(this.panX, this.panY);
    this.ctx.scale(this.zoomScale, this.zoomScale);

    const activeItems = this.drawHistory.slice(0, this.drawHistory.length - this.undoCount);
    for (const item of activeItems) {
      this.drawItem(item);
    }

    if (this.renderPostItsOnCanvas && this.postits && this.postits.length > 0) {
      for (const note of this.postits) {
        this.drawPostItOnCanvas(note);
      }
    }
    
    this.ctx.restore();
  }

  drawItem(item: DrawItem) {
    if (item.points.length < 2) return;
    
    this.ctx.save();
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    this.ctx.beginPath();
    this.ctx.strokeStyle = item.color;
    this.ctx.lineWidth = item.size;
    this.ctx.globalCompositeOperation = item.type === 'eraser' ? 'destination-out' : 'source-over';
    
    this.ctx.moveTo(item.points[0].x, item.points[0].y);

    // Smooth drawing using quadratic bezier curves
    for (let i = 1; i < item.points.length - 1; i++) {
      const p1 = item.points[i];
      const p2 = item.points[i + 1];
      const midPoint = {
        x: p1.x + (p2.x - p1.x) / 2,
        y: p1.y + (p2.y - p1.y) / 2
      };
      this.ctx.quadraticCurveTo(p1.x, p1.y, midPoint.x, midPoint.y);
    }
    
    const lastPoint = item.points[item.points.length - 1];
    this.ctx.lineTo(lastPoint.x, lastPoint.y);
    this.ctx.stroke();
    
    // Reset globalCompositeOperation just in case
    this.ctx.globalCompositeOperation = 'source-over';
    this.ctx.restore();
  }

  download() {
    this.redrawAll();
    
    if (this.postits && this.postits.length > 0) {
      const ratio = window.devicePixelRatio || 1;
      this.ctx.save();
      // Apply DPI scaling
      this.ctx.scale(ratio, ratio);
      // Apply pan and zoom
      this.ctx.translate(this.panX, this.panY);
      this.ctx.scale(this.zoomScale, this.zoomScale);
      
      for (const note of this.postits) {
        this.drawPostItOnCanvas(note);
      }
      this.ctx.restore();
    }

    const link = document.createElement('a');
    link.download = 'whiteboard.png';
    link.href = this.board.nativeElement.toDataURL();
    link.click();

    this.redrawAll();
  }

  private drawPostItOnCanvas(note: PostIt) {
    const ctx = this.ctx;
    const x = note.x;
    const y = note.y;
    const w = 150;
    const h = 150;
    const radius = 6;

    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.12)';
    ctx.shadowBlur = 10;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 4;

    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + w - radius, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
    ctx.lineTo(x + w, y + h - radius);
    ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
    ctx.lineTo(x + radius, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
    ctx.fillStyle = note.color || '#fef3c7';
    ctx.fill();
    ctx.restore();

    ctx.save();
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.08)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + w - radius, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
    ctx.lineTo(x + w, y + h - radius);
    ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
    ctx.lineTo(x + radius, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
    ctx.stroke();
    ctx.restore();

    ctx.save();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.04)';
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + w - radius, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
    ctx.lineTo(x + w, y + 24);
    ctx.lineTo(x, y + 24);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    ctx.save();
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.04)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, y + 24);
    ctx.lineTo(x + w, y + 24);
    ctx.stroke();
    ctx.restore();

    ctx.save();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
    ctx.font = "500 14px 'Inter', -apple-system, sans-serif";
    ctx.textBaseline = 'top';

    const textX = x + 12;
    const textY = y + 24 + 8;
    const maxTextWidth = w - 24;
    const lineHeight = 14 * 1.4;

    const lines = this.wrapText(note.text, maxTextWidth);
    for (let i = 0; i < lines.length; i++) {
      if (textY + i * lineHeight > y + h - 12) {
        break;
      }
      ctx.fillText(lines[i], textX, textY + i * lineHeight);
    }
    ctx.restore();
  }

  private wrapText(text: string, maxWidth: number): string[] {
    if (!text) return [];
    const paragraphs = text.split('\n');
    const lines: string[] = [];

    for (const paragraph of paragraphs) {
      if (paragraph === '') {
        lines.push('');
        continue;
      }

      const words = paragraph.split(' ');
      let currentLine = '';

      for (let i = 0; i < words.length; i++) {
        const word = words[i];
        const testLine = currentLine ? currentLine + ' ' + word : word;
        const metrics = this.ctx.measureText(testLine);
        
        if (metrics.width > maxWidth) {
          if (currentLine) {
            lines.push(currentLine);
            currentLine = word;
          } else {
            lines.push(word);
            currentLine = '';
          }
        } else {
          currentLine = testLine;
        }
      }
      if (currentLine) {
        lines.push(currentLine);
      }
    }
    return lines;
  }

  getContext() {
    this.ctx = this.board.nativeElement.getContext('2d')!;
    
    let width = this.board.nativeElement.offsetWidth;
    let height = this.board.nativeElement.offsetHeight;

    // Fallback if offsetWidth/Height are 0 (e.g. before render)
    if (!width || !height) {
      const rect = this.board.nativeElement.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
    }

    // Support High-DPI screens
    const ratio = window.devicePixelRatio || 1;
    this.board.nativeElement.width = width * ratio;
    this.board.nativeElement.height = height * ratio;
    
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
  }

  getCoords(event: MouseEvent): Point {
    const rect = this.board.nativeElement.getBoundingClientRect();
    const x = (event.clientX - rect.left - this.panX) / (this.zoomScale || 1);
    const y = (event.clientY - rect.top - this.panY) / (this.zoomScale || 1);
    return { x, y };
  }

  onMouseDown(event: MouseEvent) {
    if (this.readOnly) return;
    if (this.drawing) return;
    
    // If we undo and draw again, clear the redone future strokes
    if (this.undoCount > 0) {
      this.drawHistory.length = this.drawHistory.length - this.undoCount;
      this.undoCount = 0;
    }

    const coords = this.getCoords(event);
    coords.x = Math.round(coords.x * 10) / 10;
    coords.y = Math.round(coords.y * 10) / 10;
    
    this.currentStroke = {
      type: this.tool,
      color: this.color,
      size: this.size,
      points: [coords]
    };

    this.drawing = true;
    this.mouseDown = true;
  }

  onMouseUp(event?: MouseEvent) {
    if (!this.drawing) return;
    
    if (this.currentStroke && this.currentStroke.points.length > 1) {
      this.drawHistory.push(this.currentStroke);
      this.strokeAdded.emit(this.currentStroke);
    }
    
    this.drawing = false;
    this.mouseDown = false;
    this.currentStroke = null;
    this.redrawAll();
  }

  onMouseMove(event: MouseEvent) {
    if (!this.mouseDown || !this.drawing || !this.currentStroke) return;
    event.preventDefault();

    const newPosition = this.getCoords(event);
    newPosition.x = Math.round(newPosition.x * 10) / 10;
    newPosition.y = Math.round(newPosition.y * 10) / 10;

    const pts = this.currentStroke.points;
    const lastPt = pts[pts.length - 1];
    const dx = newPosition.x - lastPt.x;
    const dy = newPosition.y - lastPt.y;
    // 3 pixels distance check (distSq < 9) to save points and reduce payload size
    if (dx * dx + dy * dy < 9) return;

    pts.push(newPosition);
    
    // Split the stroke if it gets too long (prevents exceeding AWS Chime 2KB message limit)
    if (pts.length >= 100) {
      const lastPoint = pts[pts.length - 1];
      this.onMouseUp(event);
      
      // Start the next stroke with the exact last point of the previous stroke
      this.currentStroke = {
        type: this.tool,
        color: this.color,
        size: this.size,
        points: [lastPoint]
      };
      this.drawing = true;
      this.mouseDown = true;
      return;
    }
    
    const len = pts.length;
    const ratio = window.devicePixelRatio || 1;

    this.ctx.save();
    // Apply DPI scaling
    this.ctx.scale(ratio, ratio);
    // Apply pan and zoom transforms
    this.ctx.translate(this.panX, this.panY);
    this.ctx.scale(this.zoomScale, this.zoomScale);

    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';

    this.ctx.beginPath();
    this.ctx.strokeStyle = this.currentStroke.color;
    this.ctx.lineWidth = this.currentStroke.size;
    this.ctx.globalCompositeOperation = this.currentStroke.type === 'eraser' ? 'destination-out' : 'source-over';
    
    if (len >= 3) {
      const p1 = pts[len - 3];
      const p2 = pts[len - 2];
      const p3 = pts[len - 1];
      const mid1 = { x: p1.x + (p2.x - p1.x) / 2, y: p1.y + (p2.y - p1.y) / 2 };
      const mid2 = { x: p2.x + (p3.x - p2.x) / 2, y: p2.y + (p3.y - p2.y) / 2 };
      
      this.ctx.moveTo(mid1.x, mid1.y);
      this.ctx.quadraticCurveTo(p2.x, p2.y, mid2.x, mid2.y);
    } else {
      // Just a straight line for the first segment
      this.ctx.moveTo(pts[len - 2].x, pts[len - 2].y);
      this.ctx.lineTo(pts[len - 1].x, pts[len - 1].y);
    }
    this.ctx.stroke();
    
    this.ctx.restore();
  }

  onTouchStart(event: TouchEvent) {
    if (event.touches.length > 1) {
      this.onMouseUp();
      return;
    }
    event.preventDefault(); // Prevent scrolling when touching the canvas
    if (event.touches.length > 0) {
      const touch = event.touches[0];
      const mouseEvent = new MouseEvent('mousedown', {
        clientX: touch.clientX,
        clientY: touch.clientY,
      });
      this.onMouseDown(mouseEvent);
    }
  }

  onTouchMove(event: TouchEvent) {
    if (event.touches.length > 1) {
      return;
    }
    event.preventDefault(); // Prevent scrolling when moving on the canvas
    if (event.touches.length > 0) {
      const touch = event.touches[0];
      const mouseEvent = new MouseEvent('mousemove', {
        clientX: touch.clientX,
        clientY: touch.clientY,
      });
      this.onMouseMove(mouseEvent);
    }
  }

  onTouchEnd(event: TouchEvent) {
    if (event.touches.length > 0) {
      return;
    }
    event.preventDefault();
    this.onMouseUp();
  }

  uploadWhiteboard() {
    this.board.nativeElement.toBlob((boardBlob) => {
      if (!boardBlob) return;
      const filename = uuidv4() + '.png';
      const file = new File([boardBlob], filename, { type: 'image/png' });
      this.authService.uploadFile(file, 'image' + '/' + filename).then(() => {
        const linkPrefix = environment.linkPrefix;

        const link = `${linkPrefix}${this.envService.appName + '/' + 'image' + '/' + filename}`;
        const paramsToSend = structuredClone(this.chunkParams);
        paramsToSend.fileLink = link;
        paramsToSend.type = 'image';
        paramsToSend.origin = 'create-chunk';
        // console.log(paramsToSend)
        this.contentService.submitChunkUpload(paramsToSend).subscribe(
          (res) => {
            this.chunkService.passChunkEditEvent({
              event: 'uploadItem',
              chunkID: this.chunkParams._id,
            });
          },
          (err) => {
            this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
          }
        );
      });
    });
  }
}
